import React, { useEffect, useState } from 'react';
import { View, Text, Button } from '@tarojs/components';
import Modal from '@/components/Modal';
import { Formula, FormulaExplanation } from '@/components/Formula';
import { QUESTIONS } from '@/data/questions';
import { PROP_LABELS } from '@/data/settings';
import { GameState, quizGrantLimit, quizGrantUsage } from '@/utils/gameEngine';
import { GameAction } from '@/pages/game/reducer';
import styles from './index.module.scss';

const QuizDialog: React.FC<{ state: GameState; dispatch: React.Dispatch<GameAction>; onShuffle: () => void }> = ({ state, dispatch, onShuffle }) => {
  const [answer, setAnswer] = useState<number | null>(null);
  const quiz = state.quiz;
  useEffect(() => { setAnswer(null); }, [quiz?.id, quiz?.questionId]);
  const question = quiz ? QUESTIONS.find(q => q.id === quiz.questionId) : undefined;
  if (!quiz || !question) return null;
  const locked = quiz.selected !== null, result = quiz.feedback;
  const submitDisabled = !result && answer === null;
  const cancel = () => dispatch({ type: 'QUIZ_CANCEL', now: Date.now() });
  const claimLabel = quiz.kind === 'revive' ? '移出3张复活' : quiz.needsGrant && state.settings.quizMode !== 'always' ? '领取道具' : '使用' + PROP_LABELS[quiz.kind];
  const action = () => {
    if (result === 'wrong') dispatch({ type: 'QUIZ_CHANGE', requestId: quiz.id });
    else if (result === 'correct') {
      if (quiz.kind === 'shuffle' && state.settings.quizMode === 'always') onShuffle();
      dispatch({ type: 'QUIZ_CLAIM', requestId: quiz.id, now: Date.now() });
    } else if (answer !== null && !locked) dispatch({ type: 'QUIZ_ANSWER', requestId: quiz.id, questionId: question.id, selected: answer, now: Date.now() });
  };
  const actions = <View className={styles.actions}>
      <Button className={styles.abandon} onClick={cancel}>放弃</Button>
      <Button className={styles.primary} disabled={submitDisabled} onClick={action}
        style={{ color: submitDisabled ? '#748369' : '#ffffff', backgroundColor: submitDisabled ? '#e4ebdc' : '#368fd0' }}
      >
        {result === 'wrong' ? '下一题' : result === 'correct' ? claimLabel : '提交答案'}
      </Button>
  </View>;
  const footer = <>
    {result !== 'correct' && actions}
    <Text className={styles.footnote}>{PROP_LABELS[quiz.kind]}答题补给 {quizGrantUsage(state.quizStats)[quiz.kind]}/{quizGrantLimit(state.settings, quiz.kind)} · 答题期间暂停计时</Text>
  </>;
  return <Modal title={'答题 · ' + PROP_LABELS[quiz.kind]} onClose={cancel} variant='quiz' footer={footer}>
    <View className={styles.metadata}>
      <Text className={styles.topic}>{question.topic || (question.mathTopic ? '计算练习' : '常识')}</Text>
      <Text className={styles.reward}>{quiz.kind === 'revive' ? '答对移出3张复活' : quiz.needsGrant ? '答对补充1次' : '答对使用已有道具'}</Text>
    </View>
    <View data-question-id={question.id} data-calculation={question.mathTopic ? 'true' : 'false'}
      className={styles.question + (question.mathTopic ? ' ' + styles.calculation : '')}
    >{question.mathTopic ? <Formula text={question.stem} /> : <Text>{question.stem}</Text>}</View>
    {question.material && <View className={styles.material} data-material={question.material.id}>
      <Text className={styles.materialTitle}>{question.material.title}</Text>
      <Text>{question.material.description}</Text>
      <View className={styles.materialTable}>
        <View className={styles.materialRow}>
          {question.material.columns.map((cell, index) => <Text key={index}>{cell}</Text>)}
        </View>
        {question.material.rows.map((row, index) => <View key={index} className={styles.materialRow}>
          {row.map((cell, column) => <Text key={column}>{cell}</Text>)}
        </View>)}
      </View>
      {question.material.note && <Text>{question.material.note}</Text>}
    </View>}
    <View className={styles.options}>
      {question.options.map((option, index) => {
        const chosen = (quiz.selected ?? answer) === index;
        const correct = !!result && index === question.answer, wrong = result === 'wrong' && chosen;
        const color = wrong ? '#8b3f2e' : correct ? '#2e5828' : chosen ? '#20598a' : '#2b3c29';
        const backgroundColor = wrong ? '#fff0e9' : correct ? '#eaf5df' : chosen ? '#e7f3ff' : '#ffffff';
        const borderColor = wrong ? '#cb8a75' : correct ? '#7ea75c' : chosen ? '#4a9bdb' : '#cdd8bf';
        return <Button key={index} disabled={locked} data-quiz-option={index}
          className={styles.option + (chosen ? ' ' + styles.chosen : '')}
          style={{ color, backgroundColor, borderColor }} onClick={() => { if (!locked) setAnswer(index); }}
        >
          <Text className={styles.letter} style={{ color }}>{String.fromCharCode(65 + index)}</Text>
          <View className={styles.optionText} style={{ color }}>{question.mathTopic ? <Formula text={option} /> : <Text style={{ color }}>{option}</Text>}</View>
          {correct && <Text className={styles.mark} style={{ color }}>✓</Text>}
          {wrong && <Text className={styles.mark} style={{ color }}>×</Text>}
        </Button>;
      })}
    </View>
    {result === 'correct' && <View className={styles.claimActions} data-quiz-claim='true'>{actions}</View>}
    {result && <View className={styles.explanation + (result === 'wrong' ? ' ' + styles.wrong : '')} data-quiz-result={result}>
      <View className={styles.resultHeading}>
        <Text className={styles.resultTitle}>{result === 'correct' ? '回答正确' : '回答错误'}</Text>
        <Text>正确答案：{String.fromCharCode(65 + question.answer)}</Text>
      </View>
      {question.mathTopic ? <FormulaExplanation text={question.explanation} /> : <Text className={styles.explanationText}>{question.explanation}</Text>}
      {result === 'wrong' && <Text className={styles.lockHint}>本题已锁定，下一题继续。</Text>}
    </View>}
  </Modal>;
};
export default QuizDialog;
